/**
 * Trilinear interpolation between 8 corner values.
 *
 * @param v000 Value at (0,0,0)
 * @param v100 Value at (1,0,0)
 * @param v010 Value at (0,1,0)
 * @param v110 Value at (1,1,0)
 * @param v001 Value at (0,0,1)
 * @param v101 Value at (1,0,1)
 * @param v011 Value at (0,1,1)
 * @param v111 Value at (1,1,1)
 * @param fx Fractional position x (0-1)
 * @param fy Fractional position y (0-1)
 * @param fz Fractional position z (0-1)
 */
export function trilinearInterpolate(
    v000: number,
    v100: number,
    v010: number,
    v110: number,
    v001: number,
    v101: number,
    v011: number,
    v111: number,
    fx: number,
    fy: number,
    fz: number
): number {
    const v00 = v000 * (1 - fx) + v100 * fx;
    const v01 = v001 * (1 - fx) + v101 * fx;
    const v10 = v010 * (1 - fx) + v110 * fx;
    const v11 = v011 * (1 - fx) + v111 * fx;

    const v0 = v00 * (1 - fy) + v10 * fy;
    const v1 = v01 * (1 - fy) + v11 * fy;

    return v0 * (1 - fz) + v1 * fz;
}

/**
 * Sample a 3D scalar field at fractional voxel coordinates
 * using trilinear interpolation. For 2D fields (nz=1),
 * pass z=0 and the interpolation degenerates to bilinear.
 *
 * @param data Flat scalar field (one value per voxel,
 *        laid out as x + y*nx + z*nx*ny).
 * @param nx   Number of voxels along X.
 * @param ny   Number of voxels along Y.
 * @param nz   Number of voxels along Z.
 * @param x    Fractional voxel X coordinate.
 * @param y    Fractional voxel Y coordinate.
 * @param z    Fractional voxel Z coordinate.
 * @param backgroundValue Value returned when the sample
 *        position is completely outside the volume.
 *        Defaults to 0.
 * @param clampToEdge When true, out-of-bounds positions
 *        are clamped to the nearest edge voxel instead
 *        of returning backgroundValue. Defaults to true.
 * @returns Interpolated scalar value.
 */
export function trilinearSample(
    data: ArrayLike<number>,
    nx: number,
    ny: number,
    nz: number,
    x: number,
    y: number,
    z: number,
    backgroundValue: number = 0,
    clampToEdge: boolean = true
): number {
    let x0 = Math.floor(x);
    let y0 = Math.floor(y);
    let z0 = Math.floor(z);
    const x1 = x0 + 1;
    const y1 = y0 + 1;
    const z1 = z0 + 1;

    // Completely outside the volume.
    if (x1 < 0 || y1 < 0 || z1 < 0 || x0 >= nx || y0 >= ny || z0 >= nz) {
        if (!clampToEdge) {
            return backgroundValue;
        }
        const ci = Math.max(0, Math.min(nx - 1, Math.round(x)));
        const cj = Math.max(0, Math.min(ny - 1, Math.round(y)));
        const ck = Math.max(0, Math.min(nz - 1, Math.round(z)));
        return data[ci + cj * nx + ck * nx * ny];
    }

    // Clamp corner indices to valid range.
    if (x0 < 0) {
        x0 = 0;
    }
    if (y0 < 0) {
        y0 = 0;
    }
    if (z0 < 0) {
        z0 = 0;
    }
    const x1c = x1 >= nx ? nx - 1 : x1;
    const y1c = y1 >= ny ? ny - 1 : y1;
    const z1c = z1 >= nz ? nz - 1 : z1;

    const planeXY = nx * ny;

    return trilinearInterpolate(
        data[x0 + y0 * nx + z0 * planeXY],
        data[x1c + y0 * nx + z0 * planeXY],
        data[x0 + y1c * nx + z0 * planeXY],
        data[x1c + y1c * nx + z0 * planeXY],
        data[x0 + y0 * nx + z1c * planeXY],
        data[x1c + y0 * nx + z1c * planeXY],
        data[x0 + y1c * nx + z1c * planeXY],
        data[x1c + y1c * nx + z1c * planeXY],
        x - Math.floor(x),
        y - Math.floor(y),
        z - Math.floor(z)
    );
}

/**
 * Sample a 3D scalar field stored as one array per Z slice
 * at fractional voxel coordinates using trilinear
 * interpolation.
 *
 * Values are located at voxel centers: the value at index
 * `(i, j, k)` (slice `k`, offset `i + j*nx`) is the sample
 * at continuous coordinate `(i + 0.5, j + 0.5, k + 0.5)`,
 * so positions from `Grid.toVoxelSpace()` can be passed
 * directly without any offset. Positions outside the
 * volume clamp to the nearest edge value.
 *
 * @param slices Per-slice scalar fields (one value per
 *        voxel, laid out as `i + j*nx` within a slice).
 * @param nx Number of voxels along X.
 * @param ny Number of voxels along Y.
 * @param nz Number of voxels along Z.
 * @param x  Fractional voxel X coordinate.
 * @param y  Fractional voxel Y coordinate.
 * @param z  Fractional voxel Z coordinate.
 * @returns Interpolated scalar value.
 */
export function trilinearSampleVoxelCenters(
    slices: ReadonlyArray<ArrayLike<number>>,
    nx: number,
    ny: number,
    nz: number,
    x: number,
    y: number,
    z: number
): number {
    // Shift into the interpolator's corner convention,
    // where the value of voxel i sits at coordinate i.
    const qx = x - 0.5;
    const qy = y - 0.5;
    const qz = z - 0.5;
    const x0 = Math.max(0, Math.min(nx - 1, Math.floor(qx)));
    const y0 = Math.max(0, Math.min(ny - 1, Math.floor(qy)));
    const z0 = Math.max(0, Math.min(nz - 1, Math.floor(qz)));
    const x1 = Math.min(x0 + 1, nx - 1);
    const y1 = Math.min(y0 + 1, ny - 1);
    const z1 = Math.min(z0 + 1, nz - 1);
    const fx = Math.max(0, Math.min(1, qx - x0));
    const fy = Math.max(0, Math.min(1, qy - y0));
    const fz = Math.max(0, Math.min(1, qz - z0));
    const s0 = slices[z0];
    const s1 = slices[z1];
    return trilinearInterpolate(
        s0[x0 + y0 * nx],
        s0[x1 + y0 * nx],
        s0[x0 + y1 * nx],
        s0[x1 + y1 * nx],
        s1[x0 + y0 * nx],
        s1[x1 + y0 * nx],
        s1[x0 + y1 * nx],
        s1[x1 + y1 * nx],
        fx,
        fy,
        fz
    );
}
